import { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { FeaturesProvider } from './context/FeaturesContext';
import { ThemeProvider } from './context/ThemeContext';
import { Toaster } from './components/ui/sonner';
import ConfirmHost from './components/ui/confirm-host';
import AppUpdateWatcher from './components/AppUpdateWatcher';
import './index.css';

// Pages
import Landing from './pages/Landing';
const Plans = lazy(() => import('./pages/Plans'));
const Login = lazy(() => import('./pages/auth/Login'));
const Register = lazy(() => import('./pages/auth/Register'));
const ForgotPassword = lazy(() => import('./pages/auth/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/auth/ResetPassword'));
const Onboarding = lazy(() => import('./pages/auth/Onboarding'));
const Assessment = lazy(() => import('./pages/auth/Assessment'));
const AssessmentWelcome = lazy(() => import('./pages/auth/AssessmentWelcome'));
const PendingApproval = lazy(() => import('./pages/auth/PendingApproval'));

// User Pages
const Dashboard = lazy(() => import('./pages/user/Dashboard'));
const BiometricsPage = lazy(() => import('./pages/user/BiometricsPage'));
const DevicesPage = lazy(() => import('./pages/user/Devices'));
const HubPage = lazy(() => import('./pages/user/Hub'));
const WorkoutHistory = lazy(() => import('./pages/user/WorkoutHistory'));
const WorkoutSessionReport = lazy(() => import('./pages/user/WorkoutSessionReport'));
const NutritionPage = lazy(() => import('./pages/user/Nutrition'));
const BillingPage = lazy(() => import('./pages/user/Billing'));
const BillingSuccess = lazy(() => import('./pages/user/BillingSuccess'));
const WorkoutCalendar = lazy(() => import('./pages/user/WorkoutCalendar'));
const WorkoutRun = lazy(() => import('./pages/user/WorkoutRun'));
const Performance = lazy(() => import('./pages/user/Performance'));
const ReportsPage = lazy(() => import('./pages/user/Reports'));
const AdminReports = lazy(() => import('./pages/admin/AdminReports'));
const NotificationSettings = lazy(() => import('./pages/user/NotificationSettings'));
const AIMessagesHistory = lazy(() => import('./pages/user/AIMessagesHistory'));
const StreakSettings = lazy(() => import('./pages/user/StreakSettings'));
const WeeklyReport = lazy(() => import('./pages/user/WeeklyReport'));
const MonthlyReport = lazy(() => import('./pages/user/MonthlyReport'));
const AdvancedInsights = lazy(() => import('./pages/user/AdvancedInsights'));
const ProgressPhotos = lazy(() => import('./pages/user/ProgressPhotos'));
const ProfilePage = lazy(() => import('./pages/user/ProfilePage'));
const Family = lazy(() => import('./pages/user/Family'));
const Team = lazy(() => import('./pages/user/Team'));

// Layouts
const MainLayout = lazy(() => import('./layouts/MainLayout'));
const CoachLayout = lazy(() => import('./layouts/CoachLayout'));
const AdminLayout = lazy(() => import('./layouts/AdminLayout'));

// Coach Pages
const CoachDashboard = lazy(() => import('./pages/coach/CoachDashboard'));
const CoachClients = lazy(() => import('./pages/coach/CoachClients'));
const CoachClientDetail = lazy(() => import('./pages/coach/CoachClientDetail'));
const CoachExercises = lazy(() => import('./pages/coach/CoachExercises'));
const CoachWorkouts = lazy(() => import('./pages/coach/CoachWorkouts'));
const CoachAlertsDashboard = lazy(() => import('./pages/coach/CoachAlertsDashboard'));
const CoachComparison = lazy(() => import('./pages/coach/CoachComparison'));

// Admin Pages
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminUsers = lazy(() => import('./pages/admin/AdminUsers'));
const AIControlPanel = lazy(() => import('./pages/admin/AIControlPanel'));
const EvaluationsDashboard = lazy(() => import('./pages/admin/EvaluationsDashboard'));
const GroupsDashboard = lazy(() => import('./pages/admin/GroupsDashboard'));

// Invitation Pages
const AcceptInvitation = lazy(() => import('./pages/auth/AcceptInvitation'));
const AccountSuspended = lazy(() => import('./pages/auth/AccountSuspended'));

const ExerciseLibrary = lazy(() => import('./pages/admin/ExerciseLibrary'));
const BillingDashboard = lazy(() => import('./pages/admin/BillingDashboard'));
const LegalDocuments = lazy(() => import('./pages/admin/LegalDocuments'));
const Legal = lazy(() => import('./pages/Legal'));
import LegalGate from './components/legal/LegalGate';
import BrandSplash from './components/brand/BrandSplash';

const NotFound = lazy(() => import('./pages/NotFound'));
const Demo = lazy(() => import('./pages/Demo'));
// Loading component — pantalla de carga de marca (identidad v2.1)
const LoadingScreen = ({ message = 'Cargando…', showEscape = false }) => {
  const navigate = useNavigateOrNull();
  return (
    <BrandSplash message={message} detail={showEscape ? 'Esto tarda más de la cuenta.' : 'Un momento…'}>
      {showEscape && (
        <div className="text-center space-y-2">
          <p className="text-zinc-400 text-xs">Puedes intentar:</p>
          <div className="flex flex-wrap gap-2 justify-center">
            <button onClick={() => window.location.reload()} className="text-bronze hover:text-zinc-100 text-sm underline underline-offset-4 min-h-[44px]">Recargar la página</button>
            <span className="text-zinc-700">·</span>
            <button onClick={() => navigate && navigate('/onboarding')} className="text-bronze hover:text-zinc-100 text-sm underline underline-offset-4 min-h-[44px]">Ir a Onboarding</button>
            <span className="text-zinc-700">·</span>
            <button onClick={() => navigate && navigate('/login')} className="text-bronze hover:text-zinc-100 text-sm underline underline-offset-4 min-h-[44px]">Volver a iniciar sesión</button>
          </div>
        </div>
      )}
    </BrandSplash>
  );
};

// Hook helper — using react-router's useNavigate inside ProtectedRoute is
// already established; this just lets LoadingScreen escape paths without
// bubbling errors when the router isn't yet ready.
const useNavigateOrNull = () => {
  try { return useNavigate(); } catch { return null; }
};

// Rutas que dependen de la aprobación del plan. Solo estas redirigen
// a /pending-approval cuando el plan está en revisión. Todas las demás
// rutas bajo /app/* permanecen navegables aunque el plan esté pendiente
// (las que dependen del plan muestran <PlanInProgressNotice />).
const PLAN_DEPENDENT_PATHS = [
  '/app/hub',
  '/app/nutrition',
  '/app/calendar',
  '/app/workout',
  '/app/progress/weekly',
];

const isPlanDependentPath = (pathname) =>
  PLAN_DEPENDENT_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));

// Protected Route with intelligent routing
const ProtectedRoute = ({ children, requiredRoles = [], requireActivePlan = false }) => {
  const { user, loading, userStatus, needsOnboarding, needsAssessment, needsApproval, canAccessApp, isCoach, isSuspended, refreshStatus } = useAuth();
  const location = useLocation();
  const [slowLoading, setSlowLoading] = useState(false);

  // If `loading` stays true for more than 4s, surface escape hatches so the
  // user is never stuck on a silent screen.
  useEffect(() => {
    if (!loading) {
      setSlowLoading(false);
      return;
    }
    const t = setTimeout(() => setSlowLoading(true), 4000);
    return () => clearTimeout(t);
  }, [loading]);

  // If we have a user but userStatus is null after auth finished, try one
  // late refresh so the lifecycle redirects can run with real data.
  useEffect(() => {
    if (!loading && user && !userStatus && refreshStatus) {
      refreshStatus().catch(() => {});
    }
  }, [loading, user, userStatus, refreshStatus]);

  if (loading) {
    return <LoadingScreen message="Cargando tu cuenta…" showEscape={slowLoading} />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Check role requirements
  if (requiredRoles.length > 0) {
    const hasRole = requiredRoles.some((role) => user.roles?.includes(role));
    if (!hasRole) {
      return <Navigate to="/app/dashboard" replace />;
    }
  }

  // Routing guardrails for regular users (not coaches/admins)
  const isCoachOrAdmin = user.roles?.some(r => ['coach', 'admin', 'super_admin'].includes(r));
  
  // Check for suspended account (soft-lock) - but allow admins/coaches to bypass
  if (!isCoachOrAdmin && isSuspended() && location.pathname !== '/account-suspended') {
    return <Navigate to="/account-suspended" replace />;
  }
  
  // Una cuenta con rol de admin/coach que también usa la app como usuario
  // (p. ej. la del dueño en pruebas) sí debe atender el cuestionario que se
  // le pidió cuando está en la zona de usuario; en /admin sigue libre.
  const inUserArea = location.pathname.startsWith('/app') || location.pathname === '/pending-approval';
  if (
    isCoachOrAdmin && userStatus && inUserArea && needsAssessment() &&
    !location.pathname.startsWith('/app/billing')
  ) {
    return <Navigate to="/assessment" replace />;
  }

  if (!isCoachOrAdmin && userStatus) {
    // Always-allow paths: pages que deben renderizar pase lo que pase en el
    // ciclo de vida del usuario. /app/dashboard y rutas independientes
    // muestran <PlanInProgressNotice /> si el plan sigue en proceso —
    // dejando al usuario navegar en lugar de quedar atrapado.
    const ALWAYS_ALLOW = [
      '/app/billing/success',
      '/app/billing',
      '/app/dashboard',
      '/account-suspended',
    ];
    const isAlwaysAllowed = ALWAYS_ALLOW.some((p) => location.pathname === p || location.pathname.startsWith(`${p}/`));

    // Sprint G: el re-cuestionario del upgrade llega a /assessment con ?upgrade=1.
    // Ese usuario YA tiene cuenta y plan activo; sólo va a rehacer el cuestionario
    // para regenerar su plan al nuevo nivel. NO debe pasar por ningún rebote del
    // ciclo de vida (onboarding/assessment/approval/dashboard). Antes sólo se
    // eximía el rebote final (canAccessApp), así que un usuario cuyo
    // onboarding.mode_selected quedó vacío (cuenta de equipo/admin/prueba) era
    // mandado a /onboarding — que se percibe como "registrar cuenta nueva".
    const isUpgradeReassessment =
      location.pathname === '/assessment' &&
      new URLSearchParams(location.search).get('upgrade') === '1';

    // El cuestionario pendiente (o pedido de nuevo por el admin) se atiende
    // desde cualquier pantalla de la app, incluido el inicio; sólo cobros y
    // la cuenta suspendida quedan fuera. Sin esto, quien está en el inicio
    // nunca ve el cuestionario que el admin le pidió.
    const isBillingOrSuspended = ['/app/billing', '/account-suspended']
      .some((p) => location.pathname === p || location.pathname.startsWith(`${p}/`));
    if (
      !isUpgradeReassessment && !isBillingOrSuspended && !needsOnboarding() &&
      needsAssessment() &&
      !location.pathname.startsWith('/assessment') &&
      location.pathname !== '/pending-approval'
    ) {
      return <Navigate to="/assessment" replace />;
    }

    if (!isAlwaysAllowed && !isUpgradeReassessment) {
      // 1. Needs onboarding
      if (needsOnboarding() && location.pathname !== '/onboarding') {
        return <Navigate to="/onboarding" replace />;
      }

      // 3. Needs approval — solo bloquea rutas plan-dependientes.
      // Las páginas independientes (Biometrics, Photos, Devices, Profile,
      // Family, Settings, Billing) permanecen navegables aunque el plan
      // esté en revisión, para que el cliente pueda explorar la app.
      if (needsApproval() && requireActivePlan && isPlanDependentPath(location.pathname)) {
        return <Navigate to="/pending-approval" replace />;
      }

      // 4. User trying to access assessment/pending when they can use the app.
      // (El re-cuestionario de upgrade ya se eximió arriba, junto con el resto
      // de los rebotes del ciclo de vida.)
      if (canAccessApp()) {
        if (location.pathname === '/assessment' || location.pathname === '/pending-approval') {
          return <Navigate to="/app/dashboard" replace />;
        }
      }
    }
  }

  return children;
};

// Route that requires active plan (blocks if pending approval)
const AppRoute = ({ children }) => (
  <ProtectedRoute requireActivePlan={true}>
    {children}
  </ProtectedRoute>
);

function AppRoutes() {
  return (
    <Suspense fallback={<LoadingScreen message="Cargando…" />}>
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Landing />} />
      <Route path="/planes" element={<Plans />} />
      <Route path="/demo" element={<Demo />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/terminos" element={<Legal docType="terms" />} />
      <Route path="/privacidad" element={<Legal docType="privacy" />} />
      
      {/* Invitation Accept (Public) */}
      <Route path="/invitation/accept" element={<AcceptInvitation />} />
      
      {/* Onboarding Flow */}
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute>
            <Onboarding />
          </ProtectedRoute>
        }
      />
      
      {/* Assessment Flow */}
      <Route
        path="/assessment-welcome"
        element={
          <ProtectedRoute>
            <AssessmentWelcome />
          </ProtectedRoute>
        }
      />
      <Route
        path="/assessment"
        element={
          <ProtectedRoute>
            <Assessment />
          </ProtectedRoute>
        }
      />
      
      {/* Pending Approval */}
      <Route
        path="/pending-approval"
        element={
          <ProtectedRoute>
            <PendingApproval />
          </ProtectedRoute>
        }
      />
      
      {/* Account Suspended (Soft-lock) */}
      <Route
        path="/account-suspended"
        element={
          <ProtectedRoute>
            <AccountSuspended />
          </ProtectedRoute>
        }
      />

      {/* User Routes - Require active plan */}
      <Route
        path="/app/*"
        element={
          <AppRoute>
            <MainLayout />
          </AppRoute>
        }
      >
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="calendar" element={<WorkoutCalendar />} />
        <Route path="workout/run/by-session/:sessionId" element={<WorkoutRun />} />
        <Route path="workout/run/:calendarDayId" element={<WorkoutRun />} />
        <Route path="biometrics" element={<BiometricsPage />} />
        <Route path="devices" element={<DevicesPage />} />
        <Route path="hub" element={<HubPage />} />
        <Route path="hub/history" element={<WorkoutHistory />} />
        <Route path="hub/history/:sessionId" element={<WorkoutSessionReport />} />
        <Route path="nutrition" element={<NutritionPage />} />
        <Route path="billing" element={<BillingPage />} />
        <Route path="billing/success" element={<BillingSuccess />} />
        <Route path="settings/notifications" element={<NotificationSettings />} />
        <Route path="settings/ai-messages" element={<AIMessagesHistory />} />
        <Route path="settings/streaks" element={<StreakSettings />} />
        <Route path="progress/performance" element={<Performance />} />
        <Route path="reports" element={<ReportsPage />} />
        <Route path="progress/weekly" element={<WeeklyReport />} />
        <Route path="progress/monthly" element={<MonthlyReport />} />
        <Route path="progress/insights" element={<AdvancedInsights />} />
        <Route path="progress/photos" element={<ProgressPhotos />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="family" element={<Family />} />
        <Route path="team" element={<Team />} />
        <Route index element={<Navigate to="dashboard" replace />} />
      </Route>

      {/* Coach Routes */}
      <Route
        path="/coach/*"
        element={
          <ProtectedRoute requiredRoles={["coach", "coach_basic", "coach_pro", "coach_elite", "admin", "super_admin"]}>
            <CoachLayout />
          </ProtectedRoute>
        }
      >
        <Route path="dashboard" element={<CoachDashboard />} />
        <Route path="alerts" element={<CoachAlertsDashboard />} />
        <Route path="evaluations" element={<EvaluationsDashboard isAdmin={false} />} />
        <Route path="reports" element={<AdminReports />} />
        <Route path="clients" element={<CoachClients />} />
        <Route path="comparison" element={<CoachComparison />} />
        <Route path="clients/:clientId" element={<CoachClientDetail />} />
        <Route path="exercises" element={<CoachExercises />} />
        <Route path="workouts" element={<CoachWorkouts />} />
        <Route index element={<Navigate to="dashboard" replace />} />
      </Route>

      {/* Admin Routes */}
      <Route
        path="/admin/*"
        element={
          <ProtectedRoute requiredRoles={["admin", "super_admin"]}>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route path="dashboard" element={<AdminDashboard />} />
        <Route path="alerts" element={<CoachAlertsDashboard />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="ai-control" element={<AIControlPanel />} />
        <Route path="evaluations" element={<EvaluationsDashboard />} />
        <Route path="reports" element={<AdminReports />} />
        <Route path="groups" element={<GroupsDashboard />} />
        <Route path="exercises" element={<ExerciseLibrary />} />
        <Route path="billing" element={<BillingDashboard />} />
        <Route path="legal" element={<LegalDocuments />} />
        <Route index element={<Navigate to="dashboard" replace />} />
      </Route>

      {/* Fallback */}
      <Route path="*" element={<NotFound />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
      <FeaturesProvider>
      <BrowserRouter>
        <div className="App min-h-screen bg-zinc-950">
          {/* Noise Overlay */}
          <div className="noise-overlay" aria-hidden="true" />
          
          <AppRoutes />

          <AppUpdateWatcher />

          <LegalGate />

          <ConfirmHost />

          <Toaster
            position="top-right" 
            theme="dark"
            toastOptions={{
              style: {
                background: '#18181b',
                border: '1px solid #27272a',
                color: '#fafafa',
              },
            }}
          />
        </div>
      </BrowserRouter>
      </FeaturesProvider>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;
